import React, { useMemo } from "react";
import {
  startOfWeek,
  endOfWeek,
  eachDayOfInterval,
  isSameDay,
  format,
  parseISO,
  getHours,
  getMinutes,
} from "date-fns";
import { es } from "date-fns/locale";

import { statusMeta, DEFAULT_REASON_COLOR } from "./appointmentUtils";

const START_HOUR = 7;
const END_HOUR = 21;
const HOUR_HEIGHT = 52; // px

const HOURS = Array.from(
  { length: END_HOUR - START_HOUR + 1 },
  (_, i) => START_HOUR + i
);

const WeekView = ({ currentDate, appointments, onSelectAppointment, onSelectSlot }) => {
  const days = useMemo(() => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [currentDate]);

  const today = new Date();

  const positionOf = (appointment) => {
    const date = parseISO(appointment.scheduledAt);
    const minutesFromStart =
      (getHours(date) - START_HOUR) * 60 + getMinutes(date);
    const top = (minutesFromStart / 60) * HOUR_HEIGHT;
    const height = Math.max(
      ((appointment.durationMinutes || 30) / 60) * HOUR_HEIGHT,
      22
    );
    return { top, height };
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
      <div className="min-w-[700px]">
        {/* Day headers */}
      <div className="grid grid-cols-[3.5rem_repeat(7,minmax(0,1fr))] border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
        <div />
        {days.map((day) => {
          const isToday = isSameDay(day, today);
          return (
            <div key={day.toISOString()} className="px-1 py-2 text-center">
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                {format(day, "EEE", { locale: es })}
              </p>
              <p
                className={`mt-0.5 inline-flex items-center justify-center w-6 h-6 text-xs rounded-full ${
                  isToday
                    ? "bg-indigo-600 text-white font-bold"
                    : "text-slate-600 dark:text-slate-300 font-medium"
                }`}
              >
                {format(day, "d")}
              </p>
            </div>
          );
        })}
      </div>

      {/* Hour grid */}
      <div className="overflow-y-auto max-h-[calc(100vh-19rem)]">
        <div className="grid grid-cols-[3.5rem_repeat(7,minmax(0,1fr))]">
          {/* Hour gutter */}
          <div className="border-r border-slate-100 dark:border-slate-800">
            {HOURS.map((hour) => (
              <div
                key={hour}
                style={{ height: HOUR_HEIGHT }}
                className="relative border-b border-slate-100 dark:border-slate-800"
              >
                <span className="absolute -top-2 right-1.5 text-[10px] text-slate-400 tabular-nums">
                  {String(hour).padStart(2, "0")}:00
                </span>
              </div>
            ))}
          </div>

          {days.map((day) => {
            const dayAppointments = appointments.filter((appointment) =>
              isSameDay(parseISO(appointment.scheduledAt), day)
            );

            return (
              <div
                key={day.toISOString()}
                className="relative border-r border-slate-100 dark:border-slate-800"
              >
                {HOURS.map((hour) => (
                  <button
                    key={hour}
                    type="button"
                    onClick={() => {
                      const slot = new Date(day);
                      slot.setHours(hour, 0, 0, 0);
                      onSelectSlot(slot, hour);
                    }}
                    style={{ height: HOUR_HEIGHT }}
                    className="block w-full border-b border-slate-100 dark:border-slate-800 hover:bg-indigo-50/50 dark:hover:bg-indigo-950/20 transition-colors"
                  />
                ))}

                {dayAppointments.map((appointment) => {
                  const { top, height } = positionOf(appointment);
                  const meta = statusMeta(appointment.status);
                  const color = appointment.reason?.color || DEFAULT_REASON_COLOR;

                  return (
                    <button
                      key={appointment.id}
                      type="button"
                      onClick={() => onSelectAppointment(appointment)}
                      style={{
                        top,
                        height,
                        backgroundColor: `${color}22`,
                        borderLeft: `3px solid ${color}`,
                      }}
                      className={`absolute left-0.5 right-0.5 rounded-md px-1.5 py-0.5 text-left overflow-hidden hover:brightness-95 transition-all ${
                        appointment.status === "cancelled" ? "opacity-50 line-through" : ""
                      }`}
                    >
                      <span className="flex items-center gap-1">
                        <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${meta.dot}`} />
                        <span className="text-[10px] font-semibold text-slate-700 dark:text-slate-200 truncate">
                          {format(parseISO(appointment.scheduledAt), "HH:mm")} {appointment.contact?.name}
                        </span>
                      </span>
                      {height > 34 && appointment.reason?.name && (
                        <span className="block text-[9px] text-slate-500 dark:text-slate-400 truncate">
                          {appointment.reason.name}
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
      </div>
    </div>
  );
};

export default WeekView;
